import { type Container, Graphics, type Sprite } from 'pixi.js';
import { clamp01, smoothstep, wrap } from '../../shared/scalar';
import { ticksIntoBar, ticksIntoBeat } from '../../shared/tempo';
import { ELEMENTS, elementOfTick } from '../../sim/full/attacks/cycle';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, TAU, paintSheet } from '../paint';
import type { Look } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import { Flags, flagWhip } from './avatar-flags';
import { AVATAR_PIECES, COMPASS, ELEMENT_COLORS, HAIR, HEAD, UNIT } from './avatar-paint';

type Parts = Readonly<Record<keyof typeof AVATAR_PIECES, Shape>>;

const EXTENT = 40;
const MIDDLE = 2;
const CALM = { turn: 0.3, sweep: 0.5 } as const;
const TIP_GLOW = { radius: 6.7, rest: 0.15, beat: 0.45, decay: 4 } as const;
const QUARTER_ALPHA = { lit: 0.95, faint: 0.3 } as const;
const SWEEP = 0.25;
const ORBS = { count: 8, radius: 28, turn: 0.4, halo: 8, alpha: 0.45 } as const;

function elementColor(element: number): number {
  return ELEMENT_COLORS[wrap(element, ELEMENTS)] ?? ELEMENT_COLORS[0];
}

function createFlagDancerLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const quarters = Array.from({ length: ELEMENTS }, () => add(bodies, parts.quarter));
  const boldQuarters = Array.from({ length: ELEMENTS }, () => add(bodies, parts.boldQuarter));
  const arrow = add(bodies, parts.arrow);
  const orbHalos = Array.from({ length: ORBS.count }, () => add(glows, textures.halo));
  const orbs = Array.from({ length: ORBS.count }, () => add(bodies, parts.orb));
  const under = bodies.addChild(new Graphics());
  const outline = add(bodies, parts.torso);
  const torso = add(bodies, parts.torso);
  const over = bodies.addChild(new Graphics());
  const tipGlows = [add(glows, textures.halo), add(glows, textures.halo)];
  const hands = [add(bodies, parts.hand), add(bodies, parts.hand)];
  const head = add(bodies, parts.head);
  const hair = add(bodies, parts.hair);
  const flags = new Flags();
  const body = [torso, ...hands, head, hair, arrow, ...quarters, ...boldQuarters];
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
    sprite.scale.set(size);
  }

  return {
    hide() {
      hide(...body, outline, ...tipGlows, ...orbHalos, ...orbs);
      under.visible = over.visible = false;
    },
    place(input) {
      const { x, y, angle, scale, color, frame } = input;
      const { now, calm, light, tempo } = frame;
      const beats = (now - tempo.originTick) / tempo.ticksPerBeat;
      const into = ticksIntoBeat(now, tempo) / tempo.ticksPerBeat;
      const intoBar = ticksIntoBar(now, tempo) / tempo.ticksPerBeat;
      const element = elementOfTick(now, tempo);
      const lit = elementColor(element);
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;
      for (const sprite of body) {
        sprite.visible = true;
      }

      const unit = UNIT * scale;
      for (let index = 0; index < ELEMENTS; index += 1) {
        const turn = (index * Math.PI) / 2 - Math.PI / 2;
        const on = index === element;
        const thin = quarters[index];
        const bold = boldQuarters[index];
        if (thin === undefined || bold === undefined) {
          continue;
        }
        const shown = on ? bold : thin;
        const unseen = on ? thin : bold;
        unseen.visible = false;
        shown.position.set(x, y);
        shown.rotation = turn;
        shown.scale.set(scale);
        shown.alpha = on ? QUARTER_ALPHA.lit : QUARTER_ALPHA.faint;
        setTint(shown, elementColor(index));
      }
      const sweep = smoothstep(clamp01(intoBar / (calm ? CALM.sweep : SWEEP)));
      const pointing = (element - 1 + sweep) * (Math.PI / 2) - Math.PI / 2;
      const out = (COMPASS.radius + COMPASS.arrow / 2) * unit;
      arrow.position.set(x + Math.cos(pointing) * out, y + Math.sin(pointing) * out);
      arrow.rotation = pointing;
      arrow.scale.set(scale);
      setTint(arrow, lit);

      put(torso, 0, 0);
      setTint(torso, color);
      placeOutline(outline, torso, parts.torso.texture, REFERENCE_RADIUS, frame);

      under.visible = over.visible = true;
      flags.draw(under, over, {
        x,
        y,
        facing: angle,
        size: scale,
        beats,
        calm,
        tempo,
        teint: input.teint,
      });
      const pulse = calm ? 0 : Math.exp(-TIP_GLOW.decay * into);
      const glow = TIP_GLOW.rest + TIP_GLOW.beat * Math.max(pulse, flagWhip(beats, calm));
      for (let side = 0; side < 2; side += 1) {
        const hand = hands[side];
        const tipGlow = tipGlows[side];
        if (hand === undefined || tipGlow === undefined) {
          continue;
        }
        hand.position.set(flags.hands[side * 2] ?? x, flags.hands[side * 2 + 1] ?? y);
        hand.rotation = angle;
        hand.scale.set(scale);
        setTint(hand, input.teint);
        tipGlow.visible = true;
        tipGlow.position.set(flags.tips[side * 2] ?? x, flags.tips[side * 2 + 1] ?? y);
        tipGlow.scale.set((TIP_GLOW.radius * unit) / textures.halo.radius);
        tipGlow.alpha = glow * light.haloAlpha;
        setTint(tipGlow, flags.color);
      }

      put(head, HEAD.along, 0);
      setTint(head, input.teint);
      put(hair, HEAD.hair, 0);
      setTint(hair, HAIR);

      orbs.forEach((orb, index) => {
        const halo = orbHalos[index];
        orb.visible = frame.portrait;
        if (halo !== undefined) {
          halo.visible = frame.portrait;
        }
        if (!frame.portrait || halo === undefined) {
          return;
        }
        const around = beats * ORBS.turn * (calm ? CALM.turn : 1) + (index * TAU) / ORBS.count;
        const orbX = x + Math.cos(around) * ORBS.radius * unit;
        const orbY = y + Math.sin(around) * ORBS.radius * unit;
        const tint = elementColor(index);
        orb.position.set(orbX, orbY);
        orb.scale.set(scale);
        setTint(orb, tint);
        halo.position.set(orbX, orbY);
        halo.scale.set((ORBS.halo * unit) / textures.halo.radius);
        halo.alpha = ORBS.alpha * light.haloAlpha;
        setTint(halo, tint);
      });
      return 0;
    },
  };
}

export const AVATAR_DRAWING: ClassDrawing = {
  classId: 'avatar',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(AVATAR_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createFlagDancerLook(bodies, glows, textures, shapes),
    };
  },
};
